import React, { useEffect, useState } from "react";
import { Table, Button, Form, Spinner, Tabs, Tab, Alert } from "react-bootstrap";
import PaginacionSimple from "../../components/PaginacionSimple";
import useCuentasCliente from "../../hooks/useCuentasCliente";
import * as ingresosApi from "../../api/ingresos";
import * as gastosEsporadicosApi from "../../api/gastosEsporadicos";
import * as reportesApi from "../../api/reportes";
import { formatoNumero } from "../../utils/moneda";

const ICONO_FORMA_PAGO = { Tarjeta: "bi-credit-card", Bizum: "bi-phone", Transferencia: "bi-bank" };

function aFechaISO(fecha) {
  return fecha.toISOString().slice(0, 10);
}

function rangoPorDefecto() {
  const hasta = new Date();
  const desde = new Date();
  desde.setMonth(desde.getMonth() - 2);
  return { desde: aFechaISO(desde), hasta: aFechaISO(hasta) };
}

function nombreCuenta(c) {
  return `${c.tipo === "TarjetaCredito" ? "💳 " : ""}${c.banco} — ${c.alias || c.moneda}`;
}

/** Ventana de "Históricos": Ingresos y Gastos Esporádicos por cuenta/tarjeta, con rango de
 * fechas libre (a diferencia de las pantallas normales, que solo muestran los últimos 2 meses)
 * y exportables a Excel. */
export default function HistoricosPage() {
  const [tab, setTab] = useState("ingresos");

  return (
    <div>
      <h4 className="mb-3">Históricos</h4>
      <p className="text-muted small">Consulta Ingresos y Gastos Esporádicos por cualquier rango de fechas, y exporta el resultado a Excel.</p>

      <Tabs activeKey={tab} onSelect={(k) => setTab(k)} className="mb-3">
        <Tab eventKey="ingresos" title="Ingresos">
          <TabHistorico
            tipo="ingresos"
            soloBancarias
            listar={ingresosApi.listarHistoricoIngresos}
            exportar={reportesApi.descargarHistoricoIngresosExcel}
          />
        </Tab>
        <Tab eventKey="gastos" title="Gastos Esporádicos">
          <TabHistorico
            tipo="gastos"
            listar={gastosEsporadicosApi.listarHistoricoGastosEsporadicos}
            exportar={reportesApi.descargarHistoricoGastosExcel}
          />
        </Tab>
      </Tabs>
    </div>
  );
}

function TabHistorico({ tipo, soloBancarias = false, listar, exportar }) {
  const { cuentas: todas, cargando: cargandoCuentas } = useCuentasCliente();
  const cuentas = soloBancarias ? todas.filter((c) => c.tipo !== "TarjetaCredito") : todas;

  const [cuentaId, setCuentaId] = useState(null);
  const [{ desde, hasta }, setRango] = useState(rangoPorDefecto());
  const [items, setItems] = useState([]);
  const [pagina, setPagina] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [total, setTotal] = useState(0);
  const [cargando, setCargando] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [buscado, setBuscado] = useState(false);

  useEffect(() => {
    if (!cuentaId && cuentas.length) setCuentaId(cuentas[0].cuentaId);
  }, [cuentas, cuentaId]);

  const buscar = async (p = 1) => {
    if (!cuentaId) return;
    setCargando(true);
    try {
      const resultado = await listar(cuentaId, desde, hasta, p);
      setItems(resultado.items);
      setTotal(resultado.total);
      setTotalPaginas(Math.max(1, Math.ceil(resultado.total / resultado.tamanoPagina)));
      setPagina(p);
      setBuscado(true);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { if (cuentaId) buscar(1); }, [cuentaId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleExportar = async () => {
    setExportando(true);
    try {
      await exportar(cuentaId, desde, hasta);
    } finally {
      setExportando(false);
    }
  };

  if (cargandoCuentas) return <Spinner animation="border" />;
  if (!cuentas.length) {
    return (
      <Alert variant="info">
        {soloBancarias ? "No tienes cuentas bancarias (los Ingresos no aplican a Tarjetas de Crédito)." : "Primero crea una cuenta bancaria o tarjeta en el módulo \"Cuentas Bancarias\"."}
      </Alert>
    );
  }

  return (
    <div>
      <div className="fs-card mb-3">
        <div className="row g-2 align-items-end">
          <div className="col-12 col-md-4">
            <Form.Label className="small mb-1">Cuenta / Tarjeta</Form.Label>
            <Form.Select value={cuentaId || ""} onChange={(e) => setCuentaId(Number(e.target.value))}>
              {cuentas.map((c) => <option key={c.cuentaId} value={c.cuentaId}>{nombreCuenta(c)}</option>)}
            </Form.Select>
          </div>
          <div className="col-6 col-md-3">
            <Form.Label className="small mb-1">Desde</Form.Label>
            <Form.Control type="date" value={desde} onChange={(e) => setRango((r) => ({ ...r, desde: e.target.value }))} />
          </div>
          <div className="col-6 col-md-3">
            <Form.Label className="small mb-1">Hasta</Form.Label>
            <Form.Control type="date" value={hasta} onChange={(e) => setRango((r) => ({ ...r, hasta: e.target.value }))} />
          </div>
          <div className="col-12 col-md-2 d-flex gap-2">
            <Button variant="primary" className="w-100" onClick={() => buscar(1)} disabled={cargando}>
              {cargando ? <Spinner size="sm" animation="border" /> : "Buscar"}
            </Button>
          </div>
        </div>
      </div>

      {buscado && (
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-2 gap-2">
          <div className="text-muted small">{total} registro{total === 1 ? "" : "s"} encontrado{total === 1 ? "" : "s"}.</div>
          <Button size="sm" variant="outline-primary" onClick={handleExportar} disabled={exportando || !total}>
            {exportando ? <Spinner size="sm" animation="border" /> : <><i className="bi bi-file-earmark-excel me-1"></i>Exportar a Excel</>}
          </Button>
        </div>
      )}

      {cargando ? <Spinner animation="border" /> : (
        <div className="fs-card p-0 overflow-hidden">
          <Table responsive hover className="table-fs table-fs-responsive mb-0 align-middle">
            <thead>
              <tr>
                <th>Motivo</th>
                {tipo === "gastos" && <th>Forma de pago</th>}
                <th>Fecha</th>
                <th className="text-end">Monto</th>
              </tr>
            </thead>
            <tbody>
              {items.map((i) => (
                <tr key={tipo === "gastos" ? i.gastoEsporadicoId : i.ingresoId}>
                  <td data-label="Motivo">{i.motivo}</td>
                  {tipo === "gastos" && (
                    <td data-label="Forma de pago">
                      <i className={`bi ${ICONO_FORMA_PAGO[i.formaPago] || "bi-wallet2"} me-1 text-muted`}></i>
                      {i.formaPago}
                    </td>
                  )}
                  <td data-label="Fecha">{new Date(i.fecha).toLocaleDateString("es-ES")}</td>
                  <td data-label="Monto" className={`text-end fw-semibold ${tipo === "gastos" ? "text-fs-gasto" : "text-fs-ingreso"}`}>
                    {tipo === "gastos" ? "" : "+"}${formatoNumero(i.monto)}
                  </td>
                </tr>
              ))}
              {!items.length && (
                <tr><td colSpan={tipo === "gastos" ? 4 : 3} className="text-center text-muted py-4">
                  {buscado ? "No hay registros en ese rango de fechas." : "Elige un rango de fechas y presiona Buscar."}
                </td></tr>
              )}
            </tbody>
          </Table>
        </div>
      )}

      <PaginacionSimple pagina={pagina} totalPaginas={totalPaginas} onChange={buscar} />
    </div>
  );
}
